import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

/**
 * 글자 크기 배율 — Root 의 `--aichat-fs`(기본 1, 헤더 「가」 버튼으로 1·1.15·1.3) 를 곱한다.
 * 헤더·버튼 높이 같은 틀은 그대로 두고 읽는 글자만 키운다.
 */
const fs = (px: number) => `calc(${px}px * var(--aichat-fs, 1))`;

/* ───────── 플로팅 프레임 (PC) ───────── */

/**
 * PC 플로팅 패널 — 결재 화면 레이아웃은 그대로 두고 우측 하단에 카드로 띄운다. 값은 디자인 목업 그대로.
 * 위치는 transform 으로만 움직인다(useDraggablePanel).
 */
export const PanelFrame = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  /* 대화가 충분히 보이게 화면 높이에 가깝게 — 위·아래 여백만 남긴다 (PORTAL-15734) */
  height: min(calc(100vh - 96px), 860px);
  min-height: 360px;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: var(--dk-bg, #fff);
  border: 1px solid var(--dk-purple-line, #ebd6ea);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.16);
  animation: approval-ai-panel-in 200ms ease;
  touch-action: none;

  @keyframes approval-ai-panel-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* 헤더(첫 자식의 첫 자식)가 드래그 핸들 */
  & > * > :first-child {
    cursor: grab;
    user-select: none;
  }
  & > * > :first-child button {
    cursor: pointer;
  }
  &[data-dragging='true'] > * > :first-child {
    cursor: grabbing;
  }

  & > * {
    flex: 1;
    min-height: 0;
  }
`;

/* ───────── 채팅 뷰 ───────── */

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--dk-bg, #fff);
  font-family: Pretendard, -apple-system, sans-serif;
  color: var(--dk-text, #191f28);

  /* 답변 마크다운 색 — 공용 Markdown 컴포넌트의 토큰. 다크는 styles/appDarkMode.ts 의미 토큰(--dk-bg·text·line·purple-*)으로 바뀐다 */
  --aichat-md-link: #6a5af9;
  --aichat-md-quote-border: var(--dk-line-strong, #d1d9e0);
  --aichat-md-quote-text: var(--dk-text-strong, #59636e);
  --aichat-md-hr: var(--dk-line-strong, #d1d9e0);
  --aichat-md-code-bg: rgba(129, 139, 152, 0.12);
  --aichat-md-pre-bg: var(--dk-bg-subtle, #f6f8fa);
  --aichat-md-table-border: var(--dk-line-strong, #d1d9e0);
  --aichat-md-th-bg: var(--dk-bg-subtle, #f6f8fa);
`;

export const Header = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ isMobile }) => (isMobile ? 56 : 52)}px;
  padding: 0 8px 0 16px;
  flex-shrink: 0;
  background: linear-gradient(
    90deg,
    var(--dk-purple-soft, #fdf8fd) 0%,
    var(--dk-purple-soft, #faf3fa) 55%,
    var(--dk-purple-soft, #f3e4f2) 100%
  );
  border-bottom: 1px solid var(--dk-purple-line, #ebd6ea);
`;

export const HeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderAvatar = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #c77ac4;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 16px;
    height: 16px;
  }
`;

export const HeaderText = styled.span<{ isMobile?: boolean }>`
  font-size: ${({ isMobile }) => (isMobile ? 16 : 15)}px;
  line-height: ${({ isMobile }) => (isMobile ? 24 : 22)}px;
  font-weight: 700;
  color: var(--dk-purple-text, #a75ba4);
`;

export const HeaderBeta = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: var(--dk-purple-text, #c77ac4);
  opacity: 0.85;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

/** 글자 크기 순환 버튼 — 「가」 옆 + 개수로 현재 단계 표시 */
export const FontSizeButton = styled.button`
  border: none;
  background: none;
  height: 36px;
  min-width: 36px;
  padding: 0 8px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  color: var(--dk-text-secondary, #6b7684);
  cursor: pointer;
  border-radius: 999px;

  & > sup {
    font-size: 10px;
    margin-left: 1px;
  }

  &:hover {
    background: var(--dk-bg-strong, rgba(0, 0, 0, 0.04));
  }
`;

export const CloseButton = styled.button`
  border: none;
  background: none;
  width: 36px;
  height: 36px;
  font-family: inherit;
  font-size: 20px;
  line-height: 1;
  color: var(--dk-text-secondary, #6b7684);
  cursor: pointer;
  border-radius: 999px;

  &:hover {
    background: var(--dk-bg-strong, rgba(0, 0, 0, 0.04));
  }
`;

export const Body = styled.div<{ isMobile?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* 모바일: 대화가 짧아도 말풍선이 입력창 쪽(아래)에 붙게 — 키보드가 올라와도 최근 대화가 바로 보인다 */
  ${({ isMobile }) =>
    isMobile ? '& > :first-child { margin-top: auto; }' : ''}
`;

export const Intro = styled.div`
  font-size: ${fs(13)};
  line-height: ${fs(20)};
  color: var(--dk-text-secondary, #6b7684);
  word-break: keep-all;
`;

/** 이력 복원 중 — 본문 가운데 스피너 */
export const InitLoading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dk-purple-text, #a75ba4);
`;

export const LoadingMore = styled.div`
  align-self: center;
  font-size: ${fs(12)};
  line-height: ${fs(18)};
  color: var(--dk-text-secondary, #888d96);
`;

export const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 85%;
  background: var(--dk-accent, #0066cc);
  color: #fff;
  border-radius: 14px 14px 4px 14px;
  padding: 8px 12px;
  font-size: ${fs(14)};
  line-height: ${fs(20)};
  word-break: keep-all;
  white-space: pre-wrap;
`;

export const AiGroup = styled.div`
  align-self: flex-start;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const AiBubble = styled.div<{ isError?: boolean }>`
  background: var(--dk-purple-soft, #faf3fa);
  border: 1px solid var(--dk-purple-line, #ebd6ea);
  border-radius: 14px 14px 14px 4px;
  padding: 10px 12px;
  font-size: ${fs(14)};
  line-height: ${fs(21)};
  color: ${({ isError }) =>
    isError ? 'var(--dk-danger, #e5484d)' : 'var(--dk-text-primary, #34383d)'};
  word-break: keep-all;

  /* 말풍선 안 마크다운은 간격을 조금 좁게 */
  p {
    margin: 0 0 8px;
  }
  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 18px;
  }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;

export const Typing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--dk-purple-text, #a75ba4);

  & > i {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: currentColor;
    animation: ${blink} 1.2s infinite ease-in-out;
  }
  & > i:nth-of-type(2) {
    animation-delay: 0.2s;
  }
  & > i:nth-of-type(3) {
    animation-delay: 0.4s;
  }
`;

/** 이력에서 복원된 제안 응답 결과 한 줄 — 수락은 초록, 거절은 회색 */
export const SuggestionResolved = styled.div<{ accepted: boolean }>`
  font-size: ${fs(12)};
  line-height: ${fs(18)};
  font-weight: 700;
  color: ${({ accepted }) =>
    accepted ? '#00a05a' : 'var(--dk-text-secondary, #888d96)'};
`;

export const DoneBadge = styled.span`
  align-self: flex-start;
  font-size: ${fs(12)};
  line-height: ${fs(18)};
  font-weight: 700;
  color: #00a05a;
  background: var(--dk-bg-muted, #e6f5ee);
  border-radius: 999px;
  padding: 2px 10px;
`;

/* 반려 제안 카드 */
export const SuggestionCard = styled.div`
  align-self: flex-start;
  max-width: 90%;
  background: var(--dk-bg-muted, #fff);
  border: 1px solid var(--dk-purple-line, #ebd6ea);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SuggestionAsk = styled.span`
  font-size: ${fs(14)};
  line-height: ${fs(21)};
  font-weight: 700;
  color: var(--dk-purple-text, #a75ba4);
  word-break: keep-all;
`;

/** 확인 카드 — 「네」를 누르면 실제로 입력될 코멘트 */
export const SuggestionComment = styled.div`
  border-radius: 8px;
  background: var(--dk-bg-strong, #f7f8fa);
  padding: 8px 10px;
  font-size: ${fs(13)};
  line-height: ${fs(19)};
  color: var(--dk-text-primary, #34383d);
  white-space: pre-wrap;
  word-break: keep-all;

  & > b {
    display: block;
    margin-bottom: 2px;
    font-size: ${fs(12)};
    color: var(--dk-text-secondary, #6b7684);
  }
`;

/** 승인·반려 카드 — 같은 답변의 전달을 먼저 처리한다는 안내 */
export const SuggestionNote = styled.span`
  font-size: ${fs(12)};
  line-height: ${fs(18)};
  color: var(--dk-text-secondary, #6b7684);
`;

/* 전달 카드 (PORTAL-15736) */
export const ShareTargets = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ShareTarget = styled.div<{ missing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${fs(13)};
  line-height: ${fs(19)};
  font-weight: 600;
  color: ${({ missing }) =>
    missing ? 'var(--dk-danger, #e5484d)' : 'var(--dk-text-primary, #34383d)'};

  & > span {
    font-weight: 400;
    color: var(--dk-text-secondary, #6b7684);
  }

  & > label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 400;
    cursor: pointer;
  }
`;

export const ShareHint = styled.span`
  font-size: ${fs(12)};
  line-height: ${fs(18)};
  color: var(--dk-text-secondary, #6b7684);
`;

export const SuggestionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const SuggestionButton = styled.button<{ primary?: boolean }>`
  min-height: 34px;
  border: ${({ primary }) =>
    primary ? 'none' : '1px solid var(--dk-line-strong, #e7e9ec)'};
  border-radius: 8px;
  background: ${({ primary }) =>
    primary ? '#c77ac4' : 'var(--dk-bg-muted, #fff)'};
  color: ${({ primary }) =>
    primary ? '#fff' : 'var(--dk-text-primary, #34383d)'};
  padding: 0 14px;
  font-family: inherit;
  font-size: ${fs(13)};
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: ${({ primary }) =>
      primary ? '#b96ab6' : 'var(--dk-bg-strong, #f2f4f6)'};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/* 하단: 추천 질문 칩 + 입력 */
export const Footer = styled.div<{ isMobile?: boolean }>`
  border-top: 1px solid var(--dk-line, #f2f4f6);
  padding: ${({ isMobile }) =>
    isMobile
      ? '10px 16px calc(16px + env(safe-area-inset-bottom))'
      : '10px 16px 16px'};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  background: var(--dk-bg, #fff);
`;

/** 추천 질문 — 가로 한 줄, 좌우 스와이프(터치·트랙패드)로 넘긴다 (PORTAL-15733) */
export const Chips = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin: 0 -16px;
  padding: 0 16px 2px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/** 추천 질문 칩 — 누른 뒤 선택 표시를 남기지 않는다(같은 질문을 다시 눌러도 그대로) */
export const Chip = styled.button`
  border: 1px solid var(--dk-line-strong, #e7e9ec);
  border-radius: 999px;
  background: var(--dk-bg-muted, #fff);
  padding: 5px 12px;
  font-family: inherit;
  font-size: ${fs(13)};
  line-height: ${fs(18)};
  color: var(--dk-text-primary, #34383d);
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: var(--dk-bg-strong, #f2f4f6);
  }

  /* 클릭 뒤 남는 포커스 테두리는 숨기고, 키보드로 이동할 때만 보인다 */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid #c77ac4;
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const InputRow = styled.form`
  display: flex;
  gap: 8px;
`;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid var(--dk-line-strong, #e7e9ec);
  border-radius: 999px;
  padding: 0 14px;
  font-family: inherit;
  font-size: ${fs(14)};
  color: var(--dk-text, #191f28);
  outline: none;
  background: var(--dk-bg-muted, #fff);

  &::placeholder {
    color: var(--dk-text-dim, #b0b8c1);
  }

  &:focus {
    border-color: #c77ac4;
  }
`;

export const SendButton = styled.button`
  height: 40px;
  border: none;
  border-radius: 999px;
  background: #c77ac4;
  color: #fff;
  padding: 0 16px;
  font-family: inherit;
  font-size: ${fs(14)};
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #b96ab6;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
